import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCocktails } from "@/lib/api";
import { useSavedMatches } from "@/hooks/useSavedMatches";
import { Heart, Trash2, ArrowRight, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { printCocktail } from "@/lib/printCocktail";

export default function Saved() {
  const { saved, toggle, clear } = useSavedMatches();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCocktails().then(setItems).finally(() => setLoading(false));
  }, []);

  const savedCocktails = items.filter(c => saved.includes(c.slug));

  const clearAll = () => {
    if (savedCocktails.length === 0) return;
    clear();
    toast.success("Cleared your saved cocktails");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="saved-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div className="max-w-2xl">
          <p className="eyebrow">Your Bar Cart</p>
          <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl md:text-6xl font-bold text-cream leading-[1.05]">
            Saved for<br /><span className="italic font-serif-editor text-pink-brand">later sipping.</span>
          </h1>
          <p className="mt-4 text-cream/70 font-body">Everything you've bookmarked, in one editorial stack.</p>
        </div>
        {savedCocktails.length > 0 && (
          <button onClick={clearAll} className="inline-flex items-center gap-2 text-sm font-semibold text-cream/70 hover:text-pink-brand" data-testid="clear-saved">
            <Trash2 size={14} /> Clear all
          </button>
        )}
      </div>

      {loading ? (
        <p className="mt-14 text-center text-cream/60">Loading…</p>
      ) : savedCocktails.length === 0 ? (
        <div className="mt-16 text-center py-14 card-cream max-w-xl mx-auto">
          <Heart size={32} className="text-pink-brand mx-auto" />
          <p className="mt-4 font-serif-display text-2xl font-bold text-cream">No saves yet.</p>
          <p className="mt-2 text-cream/60 font-body">Tap the heart on any cocktail — we'll keep it here for you.</p>
          <Link to="/cocktails" className="btn-pill btn-pink mt-6 inline-flex" data-testid="browse-from-saved">
            Browse cocktails <ArrowRight size={16} />
          </Link>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6">
          {savedCocktails.map(c => (
            <SavedCard key={c.id} c={c} onRemove={() => toggle(c.slug)} />
          ))}
        </div>
      )}
    </div>
  );
}

function SavedCard({ c, onRemove }) {
  return (
    <Dialog>
      <div className="relative">
        <DialogTrigger asChild>
          <button className="card-cream overflow-hidden text-left w-full" data-testid={`saved-card-${c.slug}`}>
            <div className="aspect-[4/3] relative overflow-hidden">
              <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
              <div className="absolute top-3 left-3 chip chip-pink bg-pink text-cream border-transparent">{c.base_spirit}</div>
            </div>
            <div className="p-5">
              <p className="font-serif-display text-xl font-bold text-cream leading-tight">{c.name}</p>
              <p className="text-xs text-cream/60 mt-1">{c.strength} · {c.glassware}</p>
            </div>
          </button>
        </DialogTrigger>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute top-3 right-3 h-9 w-9 rounded-full bg-ink text-pink-brand grid place-items-center shadow-md hover:bg-pink hover:text-cream transition-colors"
          data-testid={`unsave-${c.slug}`}
          aria-label="Remove"
        >
          <Heart size={16} fill="currentColor" />
        </button>
      </div>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-ink-panel border-cream/10">
        <div className="aspect-[16/9] rounded-xl overflow-hidden -mt-2">
          <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
        </div>
        <div className="mt-4 flex items-start justify-between gap-3">
          <h2 className="font-serif-display text-3xl font-bold text-cream">{c.name}</h2>
          <button
            onClick={() => printCocktail(c)}
            className="flex-shrink-0 inline-flex items-center gap-2 h-10 px-4 rounded-full border border-cream/20 text-cream text-xs font-semibold uppercase tracking-widest hover:bg-ink hover:text-cream transition-colors"
            data-testid={`saved-print-${c.slug}`}
            aria-label="Print recipe"
          >
            <Printer size={14} /> Print
          </button>
        </div>
        <p className="text-cream/70 mt-2">{c.description}</p>
        <div className="mt-5 grid md:grid-cols-2 gap-6">
          <div>
            <p className="eyebrow">Ingredients</p>
            <ul className="mt-2 space-y-1 text-sm text-cream/85">{c.ingredients.map((i, k) => <li key={k}>· {i}</li>)}</ul>
          </div>
          <div>
            <p className="eyebrow">Method</p>
            <ol className="mt-2 space-y-2 text-sm text-cream/85 list-decimal list-inside">{c.method.map((m, k) => <li key={k}>{m}</li>)}</ol>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
